/**
 * Tiny observable store backing the download tray.
 *
 * Deliberately framework-agnostic (no React, no redux): several export helpers in
 * this codebase are module-level free functions rather than components — e.g.
 * `handleExport` in customerOnlineAccounts/table/CustomerOnlineApplicationCompletedTable.tsx,
 * which is imported by four sibling tables — so they cannot call a hook. They call
 * `downloadStore.*` directly instead, and <DownloadTray /> subscribes.
 */

export type DownloadStatus = 'downloading' | 'done' | 'error' | 'cancelled'

export interface DownloadItem {
  id: string
  /** Name the file is saved as, including extension. */
  fileName: string
  status: DownloadStatus
  /** Bytes received so far. Always meaningful. */
  loaded: number
  /**
   * Total bytes, or 0 when unknown. The report exports stream via Laravel
   * `streamDownload()` (Transfer-Encoding: chunked) and therefore send no
   * Content-Length, so this stays 0 and the tray shows an indeterminate bar.
   */
  total: number
  startedAt: number
  finishedAt?: number
  errorMessage?: string
  /** Aborts the in-flight request. Present only while status is 'downloading'. */
  cancel?: () => void
}

type Listener = (items: DownloadItem[]) => void

let items: DownloadItem[] = []
let listeners: Listener[] = []
let seq = 0

const emit = () => {
  const snapshot = items
  listeners.forEach((listener) => listener(snapshot))
}

export const downloadStore = {
  getItems: (): DownloadItem[] => items,

  subscribe: (listener: Listener): (() => void) => {
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  },

  add: (fileName: string, cancel: () => void): string => {
    const id = `dl_${++seq}`
    items = [
      ...items,
      {
        id,
        fileName,
        status: 'downloading',
        loaded: 0,
        total: 0,
        startedAt: Date.now(),
        cancel,
      },
    ]
    emit()
    return id
  },

  update: (id: string, patch: Partial<DownloadItem>) => {
    let changed = false
    items = items.map((item) => {
      if (item.id !== id) return item
      changed = true
      return {...item, ...patch}
    })
    if (changed) emit()
  },

  /** Marks an item terminal and drops its cancel handle. */
  finish: (id: string, status: Exclude<DownloadStatus, 'downloading'>, patch: Partial<DownloadItem> = {}) => {
    downloadStore.update(id, {...patch, status, finishedAt: Date.now(), cancel: undefined})
  },

  remove: (id: string) => {
    items = items.filter((item) => item.id !== id)
    emit()
  },

  /**
   * Cancels anything in flight and empties the tray. Cancellation is asynchronous, so
   * the list is cleared here rather than left to each item's 'cancelled' transition —
   * otherwise the close button would leave the just-cancelled rows on screen.
   */
  closeAll: () => {
    items.forEach((item) => item.cancel?.())
    items = []
    emit()
  },
}
